// Apolo IDE — Hook Templates library.
//
// 12 curated hooks Pedro can install with 1 click. Each template carries
// metadata + ready-to-run script + recommended config. Categories follow
// the Hook Powerstack sprint plan.
//
// Wiring status per event:
//   user_prompt_submit  — WIRED (fires from CodeComposer)
//   pre_tool_call        — WIRED (E8 super-marathon: loop/hookLifecycle.js at the tool
//                          gate; a BLOCKING hook denies the tool). Behind apolo_hooks_lifecycle.
//   post_tool_call       — WIRED (E8: observe, after each tool). Behind apolo_hooks_lifecycle.
//   stop                 — WIRED (E8: observe, at turn completion). Behind apolo_hooks_lifecycle.

/**
 * @typedef {Object} HookTemplate
 * @property {string} id              - stable slug
 * @property {string} title           - human-readable name
 * @property {string} category        - 'guard' | 'quality' | 'awareness' | 'workflow'
 * @property {string} description     - what it does
 * @property {string} event           - matches HookEvent enum from HooksView
 * @property {string} [matcher]       - regex
 * @property {string} runtime         - 'shell' | 'js-sandbox'
 * @property {string} script          - the actual script
 * @property {boolean} blocking
 * @property {boolean} enabledByDefault
 * @property {string} [requires]      - external dep needed (e.g. "gh", "prettier", "SLACK_WEBHOOK_URL env")
 * @property {string} [wiringStatus]  - 'wired' | 'pending'  (UI shows badge)
 */

/** @type {HookTemplate[]} */
export const HOOK_TEMPLATES = [
  // ============================ GUARD RAILS ============================
  {
    id: 'anti-self-destruct',
    title: '1 · Anti-self-destruct',
    category: 'guard',
    description: 'Bloqueia comandos catastróficos: rm -rf /, DROP TABLE, force-push origin main, npm publish --force. Você não precisa lembrar — Apolo lembra por você.',
    event: 'pre_tool_call',
    matcher: '^bash$',
    runtime: 'shell',
    blocking: true,
    enabledByDefault: true,
    wiringStatus: 'pending',
    script: `# Anti-self-destruct guard.
# Reads the proposed command from APOLO_COMMAND and blocks if it matches
# any pattern in the danger list. Exit 1 = block.
CMD="\${APOLO_COMMAND:-}"
DANGEROUS='rm -rf /|rm -rf ~|: > /|mkfs|dd if=|DROP TABLE|DROP DATABASE|TRUNCATE TABLE|force-push|push --force|push -f|publish --force|chmod 777 /|chown -R root|killall -9|shutdown|reboot'
if echo "$CMD" | grep -E "$DANGEROUS" > /dev/null 2>&1; then
  echo "🛑 BLOCKED: command matches dangerous pattern" >&2
  echo "Command was: $CMD" >&2
  exit 1
fi
exit 0`,
  },
  {
    id: 'sensitive-file-shield',
    title: '2 · Sensitive file shield',
    category: 'guard',
    description: 'Bloqueia edits em .env, secrets, credentials, .pem, .p12. Zero risco de commitar key sensível por acidente.',
    event: 'pre_tool_call',
    matcher: '^(write|multi_edit|edit)$',
    runtime: 'shell',
    blocking: true,
    enabledByDefault: true,
    wiringStatus: 'pending',
    script: `# Sensitive file shield.
# Blocks writes to credentials/secrets paths.
PATH_TARGET="\${APOLO_FILE_PATH:-}"
if [ -z "$PATH_TARGET" ]; then exit 0; fi
SENSITIVE='\\.env(\\.|$)|secrets/|credentials\\.|\\.pem$|\\.p12$|\\.key$|id_rsa$|id_ed25519$|/keychain'
if echo "$PATH_TARGET" | grep -E "$SENSITIVE" > /dev/null 2>&1; then
  echo "🛑 BLOCKED: refusing to edit sensitive file" >&2
  echo "Path: $PATH_TARGET" >&2
  exit 1
fi
exit 0`,
  },
  {
    id: 'daily-budget-guard',
    title: '3 · Daily tool-call budget',
    category: 'guard',
    description: 'Limita a 200 tool calls/dia. Quando estourar, bloqueia. Força você pensar antes de só pedir mais.',
    event: 'pre_tool_call',
    matcher: '',
    runtime: 'shell',
    blocking: true,
    enabledByDefault: false,
    wiringStatus: 'pending',
    script: `# Daily tool-call budget.
# Increments a per-day counter file. Blocks when ceiling reached.
LIMIT=200
DIR="$HOME/.apolo"
mkdir -p "$DIR"
TODAY=$(date +%Y-%m-%d)
FILE="$DIR/budget-$TODAY.count"
COUNT=$(cat "$FILE" 2>/dev/null || echo 0)
if [ "$COUNT" -ge "$LIMIT" ]; then
  echo "🛑 Daily budget reached ($COUNT/$LIMIT calls). Take a break." >&2
  exit 1
fi
echo $((COUNT + 1)) > "$FILE"
exit 0`,
  },

  // ============================ QUALITY ================================
  {
    id: 'auto-format',
    title: '4 · Auto-format por linguagem',
    category: 'quality',
    description: 'Roda prettier/black/ruff/gofmt/rustfmt/terraform fmt automaticamente após edits em arquivo daquela linguagem.',
    event: 'post_tool_call',
    matcher: '^(write|multi_edit|edit)$',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'prettier / black / ruff / gofmt / rustfmt / terraform (per language)',
    script: `# Auto-format dispatcher by extension.
FILE="\${APOLO_FILE_PATH:-}"
[ -z "$FILE" ] && exit 0
[ ! -f "$FILE" ] && exit 0
EXT="\${FILE##*.}"
case "$EXT" in
  js|jsx|ts|tsx|mjs|cjs|json|css|html|md|yml|yaml)
    command -v prettier > /dev/null && prettier --write "$FILE" 2>/dev/null || true ;;
  py)
    command -v ruff > /dev/null && ruff format "$FILE" 2>/dev/null || \\
    command -v black > /dev/null && black --quiet "$FILE" 2>/dev/null || true ;;
  go)
    command -v gofmt > /dev/null && gofmt -w "$FILE" 2>/dev/null || true ;;
  rs)
    command -v rustfmt > /dev/null && rustfmt --quiet "$FILE" 2>/dev/null || true ;;
  tf|tfvars)
    command -v terraform > /dev/null && terraform fmt "$FILE" > /dev/null 2>&1 || true ;;
esac
exit 0`,
  },
  {
    id: 'auto-test-loop',
    title: '5 · Auto-test loop',
    category: 'quality',
    description: 'Após edit em src/, dispara npm test em background. Se falha, insere prompt no composer pedindo fix.',
    event: 'post_tool_call',
    matcher: '^(write|multi_edit|edit)$',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'npm test no package.json + projeto com src/',
    script: `# Auto-test loop.
FILE="\${APOLO_FILE_PATH:-}"
[ -z "$FILE" ] && exit 0
case "$FILE" in
  *src/*) ;;
  *) exit 0 ;;
esac
DIR=$(pwd)
LOG="$HOME/.apolo/last-test.log"
mkdir -p "$(dirname "$LOG")"
# Run in background, dispatch event on completion.
(
  cd "$DIR" && npm test --silent > "$LOG" 2>&1
  RC=$?
  if [ "$RC" -ne 0 ]; then
    echo "FAILED: npm test exit $RC after editing $FILE" >&2
    # The renderer can read APOLO_LAST_TEST_LOG and inject prompt.
    echo "$FILE" > "$HOME/.apolo/last-test-failed-file"
  fi
) &
exit 0`,
  },
  {
    id: 'auto-typecheck',
    title: '6 · Auto-typecheck (TS)',
    category: 'quality',
    description: 'Após edit em .ts/.tsx, roda tsc --noEmit em background. Notification macOS se aparecer erro novo.',
    event: 'post_tool_call',
    matcher: '^(write|multi_edit|edit)$',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'tsc no PATH + tsconfig.json',
    script: `# Auto-typecheck on TS edits.
FILE="\${APOLO_FILE_PATH:-}"
[ -z "$FILE" ] && exit 0
case "$FILE" in
  *.ts|*.tsx) ;;
  *) exit 0 ;;
esac
(
  if ! npx tsc --noEmit > /dev/null 2>&1; then
    osascript -e 'display notification "tsc errors detected" with title "Apolo Typecheck"' 2>/dev/null || true
  fi
) &
exit 0`,
  },

  // ============================ AWARENESS ===============================
  {
    id: 'daily-activity-log',
    title: '7 · Daily activity log',
    category: 'awareness',
    description: 'Append CSV linha com session_id, tools, duration, cost a cada stop. Combina com cron pra gerar markdown report no Brain às 23:00.',
    event: 'stop',
    matcher: '',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: true,
    wiringStatus: 'pending',
    script: `# Daily activity log.
DIR="$HOME/.apolo"
mkdir -p "$DIR"
FILE="$DIR/activity-$(date +%Y-%m).csv"
# Create header if file doesn't exist.
if [ ! -f "$FILE" ]; then
  echo "timestamp,session_id,tools_used,duration_s,cost_usd" > "$FILE"
fi
TS=$(date +%Y-%m-%dT%H:%M:%S)
SESSION="\${APOLO_SESSION_ID:-unknown}"
TOOLS="\${APOLO_TOOLS_USED:-0}"
DURATION="\${APOLO_DURATION_S:-0}"
COST="\${APOLO_SESSION_COST:-0}"
echo "$TS,$SESSION,$TOOLS,$DURATION,$COST" >> "$FILE"
exit 0`,
  },
  {
    id: 'loop-detector',
    title: '8 · Loop detector',
    category: 'awareness',
    description: 'Bloqueia se a mesma tool + input apareceu 5x em 60s. Mata ciclos infinitos antes de queimar tokens.',
    event: 'pre_tool_call',
    matcher: '',
    runtime: 'shell',
    blocking: true,
    enabledByDefault: false,
    wiringStatus: 'pending',
    script: `# Loop detector — blocks when the same tool+input hash appears 5+ times
# in the last 60 seconds. Uses a per-day log file with timestamps.
DIR="$HOME/.apolo"
mkdir -p "$DIR"
TOOL="\${APOLO_TOOL:-}"
INPUT="\${APOLO_TOOL_INPUT_JSON:-}"
[ -z "$TOOL" ] && exit 0
HASH=$(echo "$TOOL|$INPUT" | shasum -a 256 | cut -d' ' -f1 | head -c 16)
LOG="$DIR/loop-detect-$(date +%Y-%m-%d).log"
NOW=$(date +%s)
echo "$NOW $HASH" >> "$LOG"
# Count occurrences of this hash in the last 60 seconds.
CUTOFF=$((NOW - 60))
COUNT=$(awk -v cutoff=$CUTOFF -v h="$HASH" '$1 >= cutoff && $2 == h' "$LOG" | wc -l | tr -d ' ')
if [ "$COUNT" -ge 5 ]; then
  echo "🛑 LOOP DETECTED: tool=$TOOL repeated $COUNT times in last 60s" >&2
  exit 1
fi
exit 0`,
  },
  {
    id: 'cost-ceiling-alarm',
    title: '9 · Cost ceiling alarm',
    category: 'awareness',
    description: 'Quando session cost > $5, notification macOS + push pro celular via Pushover (se config). Você não percebe quando agent queima dinheiro.',
    event: 'post_tool_call',
    matcher: '',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'PUSHOVER_USER + PUSHOVER_TOKEN env (opcional)',
    script: `# Cost ceiling alarm — $5.00 default.
COST="\${APOLO_SESSION_COST:-0}"
CEIL=5.00
# Float comparison via awk (bash doesn't do floats).
EXCEED=$(awk -v c="$COST" -v t="$CEIL" 'BEGIN { print (c+0 > t+0) ? 1 : 0 }')
[ "$EXCEED" -ne 1 ] && exit 0
# Throttle: only fire once per hour.
DIR="$HOME/.apolo"
mkdir -p "$DIR"
STAMP="$DIR/cost-alarm-last.ts"
NOW=$(date +%s)
LAST=$(cat "$STAMP" 2>/dev/null || echo 0)
[ $((NOW - LAST)) -lt 3600 ] && exit 0
echo "$NOW" > "$STAMP"
osascript -e "display notification \\"Session cost \\\\$$COST exceeded \\\\$$CEIL\\" with title \\"Apolo Cost Alarm\\" sound name \\"Funk\\"" 2>/dev/null || true
if [ -n "$PUSHOVER_USER" ] && [ -n "$PUSHOVER_TOKEN" ]; then
  curl -s --max-time 5 https://api.pushover.net/1/messages.json \\
    -F "token=$PUSHOVER_TOKEN" -F "user=$PUSHOVER_USER" \\
    -F "title=Apolo cost alarm" -F "message=Session cost \\$$COST exceeded ceiling \\$$CEIL" > /dev/null || true
fi
exit 0`,
  },

  // ============================ WORKFLOW ================================
  {
    id: 'auto-commit-brain-sync',
    title: '10 · Auto-commit + Brain sync',
    category: 'workflow',
    description: 'Ao stop com edits na session, git commit automático + escreve resumo da session como nota Obsidian no Brain.',
    event: 'stop',
    matcher: '',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'git + Brain MCP (escrita via filesystem)',
    script: `# Auto-commit + Brain sync.
DIR=$(pwd)
cd "$DIR" || exit 0
# Skip if not a git repo or nothing to commit.
git rev-parse --git-dir > /dev/null 2>&1 || exit 0
git diff --quiet && git diff --cached --quiet && exit 0
SESSION="\${APOLO_SESSION_ID:-unknown}"
git add -A
git -c user.name="Apolo" -c user.email="apolo@local" \\
  commit -m "auto: session $SESSION

Co-Authored-By: Apolo IDE <apolo@local>" --no-verify > /dev/null 2>&1 || true
# Brain note (optional — only if vault path exists).
VAULT="$HOME/Downloads/Pedro's Claude/Pedro's Brain/🔧 Tech & Dev"
if [ -d "$VAULT" ]; then
  NOTE="$VAULT/$(date +%Y-%m-%d) - apolo-session-$SESSION.md"
  cat > "$NOTE" <<EOF
---
title: "Apolo session $SESSION"
date: $(date +%Y-%m-%d)
type: auto-session-log
---

# Apolo session $SESSION

Auto-committed at $(date +%Y-%m-%dT%H:%M:%S).

Working dir: \\\`$DIR\\\`
EOF
fi
exit 0`,
  },
  {
    id: 'slack-session-summary',
    title: '11 · Slack DM ao terminar',
    category: 'workflow',
    description: 'Webhook pro Slack pessoal quando a session termina: tools usadas, duration, cost. Útil quando agent autônomo roda enquanto você tá em reunião.',
    event: 'stop',
    matcher: '',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'SLACK_WEBHOOK_URL env',
    script: `# Slack session summary.
[ -z "$SLACK_WEBHOOK_URL" ] && exit 0
SESSION="\${APOLO_SESSION_ID:-unknown}"
TOOLS="\${APOLO_TOOLS_USED:-?}"
DURATION="\${APOLO_DURATION_S:-?}"
COST="\${APOLO_SESSION_COST:-?}"
TEXT="🤖 Apolo finished session \\\`$SESSION\\\` — $TOOLS tool calls · \\\${DURATION}s · \\\\$$COST"
curl -s --max-time 5 -X POST -H 'Content-Type: application/json' \\
  -d "{\\"text\\": \\"$TEXT\\"}" "$SLACK_WEBHOOK_URL" > /dev/null || true
exit 0`,
  },
  {
    id: 'auto-pr-draft',
    title: '12 · Auto-PR draft',
    category: 'workflow',
    description: 'Após multi_edit numa branch com 5+ commits sem PR, cria PR draft via gh CLI. Você revisa quando quiser.',
    event: 'post_tool_call',
    matcher: '^multi_edit$',
    runtime: 'shell',
    blocking: false,
    enabledByDefault: false,
    wiringStatus: 'pending',
    requires: 'gh CLI autenticado',
    script: `# Auto-PR draft when branch has 5+ commits but no PR yet.
command -v gh > /dev/null || exit 0
git rev-parse --git-dir > /dev/null 2>&1 || exit 0
BRANCH=$(git rev-parse --abbrev-ref HEAD 2>/dev/null)
[ "$BRANCH" = "main" ] || [ "$BRANCH" = "master" ] && exit 0
# Count commits ahead of main.
AHEAD=$(git rev-list --count main..HEAD 2>/dev/null || echo 0)
[ "$AHEAD" -lt 5 ] && exit 0
# Skip if PR already exists for this branch.
gh pr view "$BRANCH" --json number > /dev/null 2>&1 && exit 0
# Open draft PR.
gh pr create --draft --fill --base main 2>/dev/null || true
exit 0`,
  },
];

// Marker comment prefixed to every installed template's script so we can
// detect installed templates later (the DB schema has no `meta` column).
const TEMPLATE_MARKER_PREFIX = '# apolo:template-id=';

export function buildScriptWithMarker(template) {
  return `${TEMPLATE_MARKER_PREFIX}${template.id}\n${template.script}`;
}

/**
 * Parse a hook's script and return its template_id if it was installed
 * from a template (matches the marker prefix). Returns null otherwise.
 */
export function extractTemplateIdFromScript(script) {
  if (typeof script !== 'string') return null;
  const firstLine = script.split('\n', 1)[0] || '';
  if (!firstLine.startsWith(TEMPLATE_MARKER_PREFIX)) return null;
  return firstLine.slice(TEMPLATE_MARKER_PREFIX.length).trim() || null;
}

/**
 * Convert a template into the body shape POST /api/code-ide/hooks expects.
 */
export function templateToHookPayload(template) {
  return {
    event: template.event,
    matcher: template.matcher || '',
    script: buildScriptWithMarker(template),
    runtime: template.runtime,
    blocking: !!template.blocking,
    enabled: template.enabledByDefault !== false,
  };
}

export const HOOK_CATEGORIES = [
  { id: 'guard', label: 'Guard rails', color: '#ef4444', description: 'Apolo te protege de você mesmo + de hallucinações do agent' },
  { id: 'quality', label: 'Auto-quality', color: '#10b981', description: 'Format/lint/test/build automatizado' },
  { id: 'awareness', label: 'Awareness', color: '#3b82f6', description: 'Visibility do que tá acontecendo' },
  { id: 'workflow', label: 'Cross-tool workflow', color: '#a855f7', description: 'Integração com Slack/GitHub/Brain/etc' },
];

export function getTemplatesByCategory(categoryId) {
  return HOOK_TEMPLATES.filter((t) => t.category === categoryId);
}

export function getTemplateById(id) {
  return HOOK_TEMPLATES.find((t) => t.id === id);
}
